import { Block, Flex } from '@aloft/toolkit-sui';

import { memo, useMemo } from 'react';

import { LoadingScreenSamFull } from '../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import type { LongLoaderProps } from '../../components/LoadingScreen/types.ts';
import { PropertyGrid } from '../../components/PropertyGrid/PropertyGrid.tsx';
import { useRentalComparablesInitialRecommendations } from '../../hooks/useRentalComparablesInitialRecommendations.ts';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { FeatureGridDataSourcesSection } from './columnSections/FeatureGridDataSourcesSection.tsx';
import { FeatureGridFeatureSections } from './columnSections/FeatureGridFeatureSections.tsx';
import { FeatureGridMainDwellingSection } from './columnSections/FeatureGridMainDwellingSection.tsx';
import { RentalFeatureGridTransactionSection } from './columnSections/RentalFeatureGridTransactionSection.tsx';
import { FeatureGridSiteSection } from './columnSections/FeatureGridSiteSection.tsx';
import { FeatureGridLabelColumnHeader } from './components/FeatureGridLabelColumnHeader.tsx';
import { FeatureGridToolbar } from './components/FeatureGridToolbar.tsx';

const rentalComparablesInitialLoadLongLoaderProps: LongLoaderProps = {
  estimatedDurationSeconds: 10,
  messageBreakpoints: [
    {
      breakpoint: 0.06,
      message: 'Searching the market for rental listings near your subject',
    },
    { breakpoint: 0.28, message: 'Ranking listings by similarity to your property' },
    { breakpoint: 0.52, message: 'Selecting the best rental comparables for your grid' },
    { breakpoint: 0.76, message: 'Saving rental comparables to your order' },
  ],
};

export const RentalFeatureGridModule = memo(() => {
  const { showRentalComparablesInitialLoad } = useRentalComparablesInitialRecommendations();

  const columnSections = useMemo(
    () => [
      FeatureGridDataSourcesSection,
      RentalFeatureGridTransactionSection,
      FeatureGridSiteSection,
      FeatureGridMainDwellingSection,
      FeatureGridFeatureSections,
    ],
    [],
  );

  return (
    <>
      <LoadingScreenSamFull
        isLoading={showRentalComparablesInitialLoad}
        wrapperProps={{ backgroundColor: 'surface1' }}
        longLoaderProps={rentalComparablesInitialLoadLongLoaderProps}
      />
      <Flex
        {...useTkv2ReferenceBarEvents()}
        flexDirection="column"
        overflow="hidden"
        width="100%"
        paddingLeft="10px"
        paddingRight="10px"
      >
        <FeatureGridToolbar />
        <Block overflow="auto" paddingRight="10px">
          <PropertyGrid
            gridType="FeatureGrid"
            columnSections={columnSections}
            listingType="rentalListing"
            labelColumnHeader={<FeatureGridLabelColumnHeader />}
          />
        </Block>
      </Flex>
    </>
  );
});
